import { getLocaleUtil } from '../../../../../main/utils/locale.util.ts';

import { CAPITAL_LETTERS_RECOGNITION_MESSAGES } from '../../../../constants/capital-letters.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { setScriptReplacement } from '../../../../slices/scripts/scripts.slice.ts';
import { ScriptReplacementKey } from '../../../../slices/scripts/scripts.slice.types.ts';

import { getCapitalLetterUtil } from '../../../managers/capital-letter.util.ts';

import { BaseAskToTapStateChangeHandlerUtil } from './base-ask-to-tap.handler.ts';

/**
 * Specialized handler for capital letters tap interaction
 */
export class CapitalLettersAskToTapStateChangeHandlerUtil extends BaseAskToTapStateChangeHandlerUtil {
  private static _instance: CapitalLettersAskToTapStateChangeHandlerUtil;

  public static get instance(): CapitalLettersAskToTapStateChangeHandlerUtil {
    if (!CapitalLettersAskToTapStateChangeHandlerUtil._instance) {
      CapitalLettersAskToTapStateChangeHandlerUtil._instance = new CapitalLettersAskToTapStateChangeHandlerUtil();
    }
    return CapitalLettersAskToTapStateChangeHandlerUtil._instance;
  }

  private constructor(
    private readonly capitalLetterUtil = getCapitalLetterUtil(),
    private readonly localeUtil = getLocaleUtil()
  ) {
    super();
  }

  protected getHandlerName(): string {
    return 'CapitalLettersAskToTap';
  }

  /**
   * Waits for a capital letter to be clicked
   */
  protected async waitForTapInteraction(): Promise<string> {
    return await this.capitalLetterUtil.waitForClick();
  }

  /**
   * Validates if the tapped letter matches the expected letter
   * and updates script replacements for feedback
   */
  protected async validateTap(tappedItem: string): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);
    const expectedLetter = step.target;
    const isCorrect = tappedItem === expectedLetter;

    if (!isCorrect) {
      // When incorrect, provide details about the letter that was tapped
      const dispatch = await this.reduxStoreUtil.getDispatch();
      const isUppercase = tappedItem === tappedItem.toUpperCase();
      const letterType = isUppercase
        ? this.localeUtil.select('learn:capital_letters_uppercase_letter')
        : this.localeUtil.select('learn:capital_letters_lowercase_letter');

      dispatch(
        setScriptReplacement({
          key: ScriptReplacementKey.IncorrectLetter,
          value: `${letterType} ${tappedItem}`,
        })
      );
    }

    // Update lastTappedItem with the appropriate message
    this.lastTappedItem = isCorrect
      ? CAPITAL_LETTERS_RECOGNITION_MESSAGES.CORRECT(tappedItem)
      : CAPITAL_LETTERS_RECOGNITION_MESSAGES.INCORRECT(tappedItem);

    return isCorrect;
  }
}

export const getCapitalLettersAskToTapStateChangeHandlerUtil = (): CapitalLettersAskToTapStateChangeHandlerUtil =>
  CapitalLettersAskToTapStateChangeHandlerUtil.instance;
